import { useCallback, useEffect, useMemo } from 'react';
import { useSelector } from 'react-redux';
import {
  getCourier,
  getCourierId,
  getCourierName,
  getCourierPrefix,
  getCourierRealm,
  getCourierSetting,
} from '../state/courierSlice';
import { getUser } from '../state/authSlice';
import { CourierUser } from '@shared/sdk-v1/models';
import { deriveMailboxNumber } from './mailbox';

declare global {
  interface Window {
    dataLayer?: Record<string, unknown>[];
  }
}

/**
 * Push a custom event onto the GTM dataLayer. No-ops unless a GTM container is
 * configured for the courier (the analytics gate). Ensures `window.dataLayer`
 * exists first, so events fired before GTM finishes loading are queued and
 * replayed by GTM once it initializes.
 */
export const pushDataLayer = (
  event: string,
  params: Record<string, unknown>,
  gtmContainerId: string | null | undefined
): void => {
  if (typeof window === 'undefined') return;
  if (!gtmContainerId || !gtmContainerId.trim()) return;

  window.dataLayer = window.dataLayer || [];
  window.dataLayer.push({ event, ...params });
};

export type TrackEvent = (
  event: string,
  params?: Record<string, unknown>
) => void;

/**
 * React hook returning a tracker bound to the current courier + user. Every
 * event automatically includes the customer's full `mailbox_number` (e.g.
 * `KJC00001`); caller-supplied params override the auto-injected base.
 */
export const useTrackEvent = (): TrackEvent => {
  const courierSetting = useSelector(getCourierSetting);
  const prefix = useSelector(getCourierPrefix);
  const courier = useSelector(getCourier);
  const user = useSelector(getUser);

  const gtmContainerId = (courierSetting as { gtmContainerId?: string })
    ?.gtmContainerId;
  const mailboxNumber = deriveMailboxNumber(
    user,
    prefix,
    courier?.mailboxPaddingLength
  );

  return useCallback(
    (event: string, params?: Record<string, unknown>) => {
      pushDataLayer(
        event,
        { mailbox_number: mailboxNumber, ...params },
        gtmContainerId
      );
    },
    [gtmContainerId, mailboxNumber]
  );
};

// ---------------------------------------------------------------------------
// Persistent analytics context (set once / refreshed on change). Pushed WITHOUT
// an `event` key so GTM Data Layer Variables pick up the latest values for every
// tag (including page_view). Pseudonymous identifiers only — never PII.
// ---------------------------------------------------------------------------

/**
 * Push a persistent context object onto the GTM dataLayer (no `event` key).
 * Same gate as {@link pushDataLayer}.
 */
export const pushAnalyticsContext = (
  context: Record<string, unknown>,
  gtmContainerId: string | null | undefined
): void => {
  if (typeof window === 'undefined') return;
  if (!gtmContainerId || !gtmContainerId.trim()) return;

  window.dataLayer = window.dataLayer || [];
  window.dataLayer.push({ ...context });
};

// Prefers the explicit currency and falls back to the legacy useJmd toggle so
// analytics reflect the courier's real operating currency (GBP, TTD, ...).
export const deriveCurrency = (
  courierSetting: { useJmd?: boolean; currency?: string } | null | undefined
): string => courierSetting?.currency ?? (courierSetting?.useJmd ? 'JMD' : 'USD');

export const deriveAccountAgeDays = (
  createdOn: Date | string | null | undefined
): number | undefined => {
  if (!createdOn) return undefined;
  const created = new Date(createdOn).getTime();
  if (Number.isNaN(created)) return undefined;
  return Math.floor((Date.now() - created) / 86_400_000);
};

const FEATURE_FLAGS: Array<[flag: string, label: string]> = [
  ['isShopEnabled', 'shop'],
  ['isShopForMeEnabled', 'shop_for_me'],
  ['isLocalDeliveryEnabled', 'local_delivery'],
  ['seaShippingEnabled', 'sea_shipping'],
];

interface AnalyticsContextInput {
  courierId?: number;
  courierName?: string;
  realm?: string;
  courierSetting?: Record<string, unknown> | null;
  user?: CourierUser | null;
}

/**
 * Build the persistent analytics context — pseudonymous dimensions only. Keys
 * whose source is undefined are omitted (so we never push `user_id: undefined`
 * before login).
 */
export const buildAnalyticsContext = (
  input: AnalyticsContextInput
): Record<string, unknown> => {
  const { courierId, courierName, realm, courierSetting, user } = input;
  const context: Record<string, unknown> = {};

  if (courierId !== undefined) context.courier_id = courierId;
  if (courierName !== undefined) context.courier_name = courierName;
  if (realm !== undefined) context.realm = realm;
  context.currency = deriveCurrency(courierSetting);
  context.features_enabled = FEATURE_FLAGS.filter(
    ([flag]) => !!courierSetting?.[flag]
  ).map(([, label]) => label);

  if (user) {
    if (user.id !== undefined) context.user_id = user.id;
    context.email_verified = !!user.emailVerified;
    context.phone_verified = !!user.phoneVerified;
    context.account_verified = !!(user.emailVerified && user.phoneVerified);
    context.is_business = !!(user as { isBusiness?: boolean }).isBusiness;
    context.points_balance = (user as { points?: number }).points ?? 0;
    const ageDays = deriveAccountAgeDays(
      (user as { createdOn?: Date | string }).createdOn
    );
    if (ageDays !== undefined) context.account_age_days = ageDays;
  }

  return context;
};

/**
 * Mount once at the app root. Pushes the persistent analytics context whenever
 * the courier or user changes (guarded so an empty context isn't pushed before
 * anything loads, and re-pushes only when a tracked value actually changes).
 */
export const useAnalyticsContext = (): void => {
  const courierId = useSelector(getCourierId);
  const courierName = useSelector(getCourierName);
  const realm = useSelector(getCourierRealm);
  const courierSetting = useSelector(getCourierSetting);
  const user = useSelector(getUser);

  const gtmContainerId = (courierSetting as { gtmContainerId?: string })
    ?.gtmContainerId;

  const context = useMemo(
    () =>
      buildAnalyticsContext({ courierId, courierName, realm, courierSetting, user }),
    [courierId, courierName, realm, courierSetting, user]
  );
  const signature = JSON.stringify(context);

  useEffect(() => {
    if (!courierId && !user) return;
    pushAnalyticsContext(context, gtmContainerId);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [signature, gtmContainerId]);
};
